import './ReceiptPage.css';
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { MER_EVSE_ID, MER_LOCATION_NAME, MER_ORDER_ID, MER_STOP_PRESSED } from "../Start/StartPage";
import SmileySad from '../../assets/icon-smiley-bad.svg';
import SmileyAverage from '../../assets/icon-smiley-average.svg';
import SmileyGood from '../../assets/icon-smiley-good.svg';
import HeartLogo from '../../assets/symbol-heart.svg';
import 'jspdf-autotable'
import { getSession, postFeedback } from "../../Api/Api";
import { OrderSession } from "../../Model/Session";
import ErrorPage, { ERROR_PAGE_TYPE } from "../Error/ErrorPage";
import { getReceiptPdf } from "../../Common/Pdf";
import {
    onReceiptPageView,
    registerFeedbackFormOpened,
    registerFeedbackSent, registerReceiptDownload
} from "../../Api/GTM";
import { SESSION_PAYMENT_SUCCESS_SENT } from "../Session/SessionPage";

const enum SESSION_STATUS {
    INITIAL,
    LOADED,
    NOT_FOUND
}

const enum FEEDBACK_OPTION {
    BAD,
    AVERAGE,
    GOOD
}

const enum FEEDBACK_FORM_STATE {
    CLOSED,
    OPEN,
    SENT
}

export default function ReceiptPage() {
    const [orderSession, setOrderSession] = useState<{ orderSession?: OrderSession, status: SESSION_STATUS }>
        ({ orderSession: undefined, status: SESSION_STATUS.INITIAL });
    const [textAreaValue, setTextAreaValue] = useState<String>("");
    const [selectedFeedbackOption, setFeedbackOption] = useState<FEEDBACK_OPTION | undefined>();
    const [feedbackFormState, setFeedbackFormState] = useState(FEEDBACK_FORM_STATE.CLOSED);
    const history = useNavigate();


    let { orderId } = useParams<{ orderId: string }>();
    if (!orderId) {
        history("/vipps")
    }

    // Cleanup local storage
    localStorage.removeItem(MER_STOP_PRESSED);
    localStorage.removeItem(SESSION_PAYMENT_SUCCESS_SENT);


    useEffect(() => {
        if (orderId) {
            getSession(orderId).then((response) => {
                if (localStorage.getItem(MER_ORDER_ID) && response.data.session.totalPrice.includeVat) {
                    onReceiptPageView(response.data.session.totalPrice.includeVat);
                }
                localStorage.removeItem(MER_ORDER_ID);

                const data: OrderSession = response.data;
                if (data.session && data.session.connector) {
                    data.session.connector.tariff = data.session.connector.tariff.replace("0.0kr/min+", "");
                    data.session.connector.tariff_Unreg = data.session.connector.tariff_Unreg.replace("0.0kr/min+", "");
                }

                setOrderSession({ orderSession: data, status: SESSION_STATUS.LOADED })
            }).catch(e => {
                localStorage.removeItem(MER_ORDER_ID);
                setOrderSession({ orderSession: undefined, status: SESSION_STATUS.NOT_FOUND })
            }
            );
        }
    }, [orderId])

    function openFeedbackForm(feedbackOption: FEEDBACK_OPTION) {
        registerFeedbackFormOpened();
        setFeedbackOption(feedbackOption);
        setTimeout(() => {
            setFeedbackFormState(FEEDBACK_FORM_STATE.OPEN);
        }, 1000);
    }


    function getChargingTime() {
        if (orderSession.orderSession) {
            const from: Date = new Date(orderSession.orderSession.session.startDate);
            const to: Date = new Date(orderSession.orderSession.session.endDate)
            const diffMs = (to.getTime() - from.getTime());
            return Math.round(diffMs / 60000);
        }
    }

    // The API sends a naive timestamp. Keep the existing +1 hour shift, but do the arithmetic
    // on UTC fields so the result never depends on the device time zone or locale.
    function getShiftedEndDate() {
        const parsed = new Date(Date.parse(orderSession.orderSession!.session.endDate.toString()));
        return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(),
            parsed.getHours() + 1, parsed.getMinutes(), parsed.getSeconds()));
    }

    function pad(value: number) {
        return value.toString().padStart(2, "0");
    }

    function getEndTimeFormatted() {
        const date = getShiftedEndDate();
        return `${getEndDateFormatted()} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
    }
    function getEndDateFormatted() {
        const date = getShiftedEndDate();
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
    }

    function formatPrice(price?: number): string {
        if (price === undefined || price === null) return "";
        return price.toFixed(2).replace(".", ",") + " NOK";
    }



    function sendFeedback() {
        if (orderId) {
            let rating = 821200002;
            if (FEEDBACK_OPTION.BAD.valueOf() === selectedFeedbackOption) {
                rating = 821200000;
            } else if (FEEDBACK_OPTION.AVERAGE.valueOf() === selectedFeedbackOption) {
                rating = 821200001;
            }
            if (orderId) {
                postFeedback(orderId, {
                    "case": {
                        "type": "Vipps Feedback",
                        "orderId": orderId,
                        "rating": rating,
                        "comment": textAreaValue
                    }
                }).then(res => {
                    registerFeedbackSent(rating);
                    setFeedbackFormState(FEEDBACK_FORM_STATE.SENT);
                }).catch(e => {
                    console.log(e)
                })
            }
        }
    }

    function close() {
        const evseId = localStorage.getItem(MER_EVSE_ID) || "";
        history(`/vipps/${evseId}`);
    }

    if (orderSession.status === SESSION_STATUS.NOT_FOUND) {
        return <ErrorPage errorType={ERROR_PAGE_TYPE.SESSION_NOT_FOUND} />
    }

    if (!orderSession.orderSession) {
        return <></>
    }

    const locationName = localStorage.getItem(MER_LOCATION_NAME) || "";

    return (
        <div className="receipt-app">

            <header className="receipt-topbar">
                <h2>Ladesammendrag</h2>
            </header>

            <div className="receipt-body">

                {feedbackFormState !== FEEDBACK_FORM_STATE.SENT &&
                    <div className="receipt-card">
                        <span className="receipt-card-notch" role="presentation" />
                        <span className="receipt-card-bolt" role="presentation" />

                        <h2 className="receipt-location">{locationName}</h2>
                        <p className="receipt-date">{getEndTimeFormatted()}</p>

                        <ul className="receipt-details">
                            <li className="receipt-row">
                                <span>Total mottatt energi</span>
                                <span>{orderSession.orderSession?.session.kwh} kWh</span>
                            </li>
                            <li className="receipt-row">
                                <span>Total ladetid</span>
                                <span>{getChargingTime()} min</span>
                            </li>
                            <li className="receipt-divider" />
                            <li className="receipt-row receipt-row--sum">
                                <span>Total sum</span>
                                <span>{formatPrice(orderSession.orderSession?.session.totalPrice.includeVat)}</span>
                            </li>
                            <li className="receipt-vat">Alle priser er inkludert MVA</li>
                            <li className="receipt-order">Order id: {orderId}</li>
                            <li onClick={() => {
                                if (orderSession.orderSession && orderId) {
                                    registerReceiptDownload();
                                    getReceiptPdf(orderSession.orderSession, orderId, getEndDateFormatted(), getEndTimeFormatted()).save('kvittering.pdf');
                                }
                            }} className="receipt-download">
                                Last ned kvitteringen din her
                            </li>
                        </ul>
                    </div>
                }

                {feedbackFormState === FEEDBACK_FORM_STATE.SENT &&
                    <div className="receipt-thanks">
                        <img src={HeartLogo} alt="" />
                        <h2>Tusen takk!</h2>
                        <p>Din tilbakemelding hjelper oss å bli bedre.</p>
                    </div>
                }

                <div className="receipt-bottom">
                    {feedbackFormState !== FEEDBACK_FORM_STATE.SENT &&
                        <div className="receipt-feedback">
                            <p className="receipt-feedback-title">Takk for at du ladet med Mer</p>
                            <p className="receipt-feedback-subtitle">Hvordan var ladeopplevelsen din?</p>
                            <ul className="feedback-options">
                                <li onClick={() => {
                                    openFeedbackForm(FEEDBACK_OPTION.GOOD);
                                }}
                                    className={selectedFeedbackOption === FEEDBACK_OPTION.GOOD ? "feedback-option feedback-option--animate" : "feedback-option"}
                                    data-demo="open-feedback">
                                    <img src={SmileyGood} alt="God erfaring" />
                                </li>
                                <li onClick={() => {
                                    openFeedbackForm(FEEDBACK_OPTION.AVERAGE);
                                }}
                                    className={selectedFeedbackOption === FEEDBACK_OPTION.AVERAGE ? "feedback-option feedback-option--animate" : "feedback-option"}
                                    data-demo="open-feedback">
                                    <img src={SmileyAverage} alt="Grei erfaring" />
                                </li>
                                <li onClick={() => {
                                    openFeedbackForm(FEEDBACK_OPTION.BAD);
                                }}
                                    className={selectedFeedbackOption === FEEDBACK_OPTION.BAD ? "feedback-option feedback-option--animate" : "feedback-option"}
                                    data-demo="open-feedback">
                                    <img src={SmileySad} alt="Dårlig erfaring" />
                                </li>
                            </ul>
                        </div>
                    }

                    <button onClick={() => close()} className="receipt-close">Lukk</button>
                </div>
            </div>

            <div className={"feedback-form " +
                (feedbackFormState === FEEDBACK_FORM_STATE.OPEN ? "feedback-form--active" : "feedback-form--gone")}>

                {selectedFeedbackOption === FEEDBACK_OPTION.BAD &&
                    <>
                        <img src={SmileySad} alt="Dårlig erfaring" />
                        <h2>Åh nei!</h2>
                        <p>Detta er ikke godt nok - fortell oss hva som gikk galt!</p>
                    </>
                }

                {selectedFeedbackOption === FEEDBACK_OPTION.AVERAGE &&
                    <>
                        <img src={SmileyAverage} alt="Gjennomsnittlig" />
                        <h2>Hmmmm!</h2>
                        <p>Fortell oss gjerne hva vi kan bli enda bedre på</p>
                    </>
                }

                {selectedFeedbackOption === FEEDBACK_OPTION.GOOD &&
                    <>
                        <img src={SmileyGood} alt="Dårlig erfaring" />
                        <h2>Fantastisk!</h2>
                        <p>Fortell oss gjerne hvorfor ladeopplevelsen var bra!</p>
                    </>
                }

                <textarea onChange={(event) => {
                    setTextAreaValue(event.target.value)
                }
                } className="feedback-msg" placeholder="Trykk her for å skrive meldingen" />

                <div className="feedback-actions">
                    <button onClick={() => {
                        setFeedbackFormState(FEEDBACK_FORM_STATE.CLOSED)
                    }} className="feedback-cancel" data-demo="close-feedback">Avbryt
                    </button>
                    <button onClick={() => {
                        sendFeedback();
                    }} className="feedback-submit" data-demo="close-feedback">Send inn
                    </button>
                </div>
            </div>

        </div>
    );
}
